import clsx from "clsx";
import React from "react";
import environment from "web/script/modules/environment";
import * as styles from "./product-gallery-image.css";

export interface Image {
    id: string | number;
    altText?: string | null;
    url: string;
    src?: string;
}

enum LazyLoadTypes {
    EAGER = "eager",
    LAZY = "lazy",
}

type ProductGalleryImageProps = {
    src: string;
    alt?: string;
    onClick?: (event: React.MouseEvent<HTMLImageElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLImageElement>) => void;
    width?: number;
    height?: number;
    isFullscreen?: boolean;
    lazy?: boolean;
};

function buildUrl(url: string, width: number, height: number): string {
    return url.replace("/photos/", `/${width}/${height}/n/photos/`);
}

export function ProductGalleryImage({
    src,
    alt,
    lazy = false,
    onClick,
    onKeyDown,
    width = 520,
    height = 650,
    isFullscreen = false,
}: ProductGalleryImageProps): React.ReactElement {
    // CW-2172 increase dimension of images in IS PDP
    const showHigherResolutionImages = environment.getFeature(
        "seo_higher_resolution_pdp_images_uk"
    );
    const mobileRatio = { width: 520, height: 650 };
    const desktopRatio = { width: 1040, height: 1300 };

    return (
        <div
            className={clsx({
                [styles.item]: !isFullscreen,
                [styles.fullScreenItem]: isFullscreen,
            })}
            onClick={onClick}
            onKeyDown={onKeyDown}
            role="button"
            tabIndex={0}
        >
            {showHigherResolutionImages ? (
                <picture
                    className={clsx({
                        [styles.image]: !isFullscreen,
                        [styles.fullscreenImage]: isFullscreen,
                    })}
                >
                    <source
                        media={styles.mobileMedia}
                        srcSet={buildUrl(src, mobileRatio.width, mobileRatio.height)}
                    />
                    <source
                        media={styles.desktopMedia}
                        srcSet={buildUrl(src, desktopRatio.width, desktopRatio.height)}
                    />
                    <img
                        decoding="async"
                        alt={alt}
                        loading={lazy ? LazyLoadTypes.LAZY : LazyLoadTypes.EAGER}
                        width={width}
                        src={buildUrl(src, desktopRatio.width, desktopRatio.height)}
                    />
                </picture>
            ) : (
                <img
                    className={clsx({
                        [styles.image]: !isFullscreen,
                        [styles.fullscreenImage]: isFullscreen,
                    })}
                    decoding="async"
                    src={buildUrl(src, width, height)}
                    alt={alt}
                    loading={lazy ? LazyLoadTypes.LAZY : LazyLoadTypes.EAGER}
                    width={width}
                />
            )}
        </div>
    );
}
